import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { TrendingUp, Star, ArrowUp } from 'lucide-react'

interface QualityScoreProps {
  before: number
  after: number
  label: string
  icon?: React.ReactNode
  showProgress?: boolean
}

export function QualityScore({
  before,
  after,
  label,
  icon,
  showProgress = true
}: QualityScoreProps) {
  const improvement = after - before
  const improvementPercent = before > 0 ? Math.round((improvement / before) * 100) : 0

  // Determine quality tier
  const getQualityTier = (score: number) => {
    if (score >= 90) return { color: 'text-green-600', bg: 'bg-green-50', label: 'Excellent' }
    if (score >= 75) return { color: 'text-blue-600', bg: 'bg-blue-50', label: 'Good' }
    if (score >= 60) return { color: 'text-yellow-600', bg: 'bg-yellow-50', label: 'Fair' }
    return { color: 'text-red-600', bg: 'bg-red-50', label: 'Poor' }
  }

  const beforeTier = getQualityTier(before)
  const afterTier = getQualityTier(after)

  // Get star rating
  const getStarRating = (score: number) => {
    if (score >= 90) return 5
    if (score >= 75) return 4
    if (score >= 60) return 3
    if (score >= 40) return 2
    return 1
  }

  const stars = getStarRating(after)

  return (
    <div className="space-y-3">
      {/* Header with icon and label */}
      <div className="flex items-center gap-2">
        {icon}
        <span className="font-medium text-sm">{label}</span>
        <Badge
          variant="outline"
          className={`${afterTier.bg} ${afterTier.color} border-current`}
        >
          {afterTier.label}
        </Badge>
      </div>

      {/* Score display with improvement */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <span className={`text-lg font-bold ${afterTier.color}`}>
            {after}
          </span>
          <span className="text-xs text-muted-foreground">/100</span>
        </div>

        {improvement > 0 && (
          <div className="flex items-center gap-1 text-green-600">
            <ArrowUp className="h-3 w-3" />
            <span className="text-xs font-medium">+{improvement}</span>
            <span className="text-xs text-muted-foreground">({improvementPercent}%)</span>
          </div>
        )}
      </div>

      {/* Star rating */}
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`h-4 w-4 ${
              i < stars
                ? 'fill-yellow-400 text-yellow-400'
                : 'fill-gray-200 text-gray-200'
            }`}
          />
        ))}
      </div>

      {/* Progress bar */}
      {showProgress && (
        <div className="space-y-1">
          <Progress
            value={after}
            className="h-2"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Before: {before}</span>
            <span>After: {after}</span>
          </div>
        </div>
      )}

      {/* Before/After comparison */}
      {improvement > 0 && (
        <div className="flex items-center gap-2 text-xs">
          <span className={`${beforeTier.color} line-through`}>
            {beforeTier.label}
          </span>
          <TrendingUp className="h-3 w-3 text-green-600" />
          <span className={`${afterTier.color} font-medium`}>
            {afterTier.label}
          </span>
        </div>
      )}
    </div>
  )
}

// Helper function to calculate quality scores
export function calculateQualityScore(
  before: string,
  after: string,
  type: 'question' | 'answer' | 'tags' | 'variations'
): { before: number; after: number } {
  const getQuestionScore = (text: string) => {
    let score = 0

    // Length scoring (optimal 8-15 words)
    const wordCount = text.split(/\s+/).length
    if (wordCount >= 8 && wordCount <= 15) score += 30
    else if (wordCount >= 5 && wordCount <= 20) score += 20
    else score += 10

    // Question format
    if (text.includes('?')) score += 20
    if (/^(Wie|Was|Warum|Wo|Wann|Welche|Kann|Können|Darf|Dürfen|Soll|Sollen)/i.test(text)) score += 20

    // Clarity indicators
    if (!text.includes('!!')) score += 10
    if (!/^\s*[A-Z]/.test(text) === false) score += 10

    // Specificity
    const specificWords = ['installieren', 'konfigurieren', 'einrichten', 'verbinden', 'Problem', 'Lösung']
    const hasSpecificWords = specificWords.some(word => text.toLowerCase().includes(word))
    if (hasSpecificWords) score += 10

    return Math.min(100, score)
  }

  const getAnswerScore = (text: string) => {
    let score = 0

    // Length scoring (comprehensive but not too long)
    const wordCount = text.split(/\s+/).length
    if (wordCount >= 50 && wordCount <= 300) score += 25
    else if (wordCount >= 30 && wordCount <= 500) score += 15
    else score += 5

    // Markdown structure
    if (text.includes('#')) score += 15  // Has headers
    if (text.includes('##')) score += 10 // Has subheaders
    if (text.includes('\n-') || text.includes('\n*')) score += 15 // Has lists
    if (text.includes('**') || text.includes('*')) score += 10 // Has formatting

    // Completeness
    if (text.toLowerCase().includes('schritt') || text.toLowerCase().includes('step')) score += 10
    if (text.includes('http')) score += 10 // Has links
    if (text.toLowerCase().includes('wichtig') || text.toLowerCase().includes('achtung')) score += 5

    // Readability
    const sentences = text.split(/[.!?]+/).length
    const avgWordsPerSentence = wordCount / Math.max(sentences, 1)
    if (avgWordsPerSentence >= 10 && avgWordsPerSentence <= 20) score += 10

    return Math.min(100, score)
  }

  const getTagsScore = (tags: string | string[]) => {
    const tagArray = Array.isArray(tags) ? tags : (tags ? tags.split(',').map(t => t.trim()) : [])
    let score = 0

    // Quantity scoring (optimal 3-5 tags)
    if (tagArray.length >= 3 && tagArray.length <= 5) score += 30
    else if (tagArray.length >= 2 && tagArray.length <= 7) score += 20
    else score += 10

    // 2-word format scoring
    const twoWordTags = tagArray.filter(tag => tag.split(/\s+/).length === 2)
    const twoWordRatio = tagArray.length > 0 ? twoWordTags.length / tagArray.length : 0
    score += Math.round(twoWordRatio * 40)

    // Length scoring (not too short, not too long)
    const wellSizedTags = tagArray.filter(tag => tag.length >= 5 && tag.length <= 20)
    const wellSizedRatio = tagArray.length > 0 ? wellSizedTags.length / tagArray.length : 0
    score += Math.round(wellSizedRatio * 20)

    // Bonus for variety (no duplicates)
    const uniqueTags = [...new Set(tagArray.map(t => t.toLowerCase()))]
    if (uniqueTags.length === tagArray.length) score += 10

    return Math.min(100, score)
  }

  const getVariationsScore = (variations: string) => {
  const variationArray = variations.split(',').map(v => v.trim()).filter(v => v.length > 0)
  let score = 0

  // Quantity scoring (optimal 2-4 variations)
  if (variationArray.length >= 2 && variationArray.length <= 4) score += 40
  else if (variationArray.length >= 1 && variationArray.length <= 5) score += 25
  else score += 10

  // Length scoring (not too short, not too long)
  const wellSizedVariations = variationArray.filter(v => v.split(/\s+/).length >= 3 && v.split(/\s+/).length <= 10)
  const wellSizedRatio = variationArray.length > 0 ? wellSizedVariations.length / variationArray.length : 0
  score += Math.round(wellSizedRatio * 30)

  // Diversity scoring (different question words)
  const questionWords = ['wie', 'was', 'warum', 'wo', 'wann', 'welche', 'kann', 'können', 'darf', 'dürfen', 'soll', 'sollen']
  const uniqueQuestionWords = new Set()

  variationArray.forEach(variation => {
    const firstWord = variation.toLowerCase().split(/\s+/)[0]
    if (questionWords.includes(firstWord)) {
      uniqueQuestionWords.add(firstWord)
    }
  })

  score += Math.min(30, uniqueQuestionWords.size * 10)

  return Math.min(100, score)
}

const beforeScore = type === 'question' ? getQuestionScore(before) :
                     type === 'answer' ? getAnswerScore(before) :
                     type === 'tags' ? getTagsScore(before) :
                     getVariationsScore(before)

  const afterScore = type === 'question' ? getQuestionScore(after) :
                    type === 'answer' ? getAnswerScore(after) :
                    type === 'tags' ? getTagsScore(after) :
                    getVariationsScore(after)

  return { before: beforeScore, after: afterScore }
}